import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../contexts/ToastContext';
import { updateAutoSyncPref } from '../redux/slices/djAutoSyncSlice';
import { AUTO_SYNC_NUDGE_MAX, getAutoSyncNudgeCount, markAutoSyncNudgeShown } from '../utils/djAutoSync';
import { formatDjImportSummary } from '../utils/djImportSummary';
import { track } from '../utils/analytics';

/**
 * "Turn on Auto-sync" offered at the two moments it lands best: right after
 * the tool's FIRST import (the user just connected — finish the setup), and
 * right after a manual sync triggered by a change notification (the user just
 * did the chore it removes). Returns maybeNudge(result, from, { firstSync })
 * — shows a card toast (replacing the plain success toast) and returns true
 * when the offer fires; capped at AUTO_SYNC_NUDGE_MAX showings per app, never
 * shown once Auto-sync is on.
 */
export default function useAutoSyncNudge(app, { label, noun = 'playlist', logo }) {
    const dispatch = useDispatch();
    const toast = useToast();
    const userId = useSelector((s) => s.auth?.user?.id) || null;
    const enabled = useSelector((s) => !!s.djAutoSync.prefs[app]?.enabled);

    return useCallback((result, from, { firstSync = false } = {}) => {
        // Routine re-syncs aren't the moment — only the first import ever, or
        // a change-driven sync.
        const changeDriven = from === 'watcher_card' || from === 'banner';
        if (enabled || (!firstSync && !changeDriven)) return false;
        if (getAutoSyncNudgeCount(app, userId) >= AUTO_SYNC_NUDGE_MAX) return false;
        markAutoSyncNudgeShown(app, userId);
        track('dj_auto_sync_nudge_shown', { app, from, first_sync: firstSync });
        toast.card({
            artwork: logo,
            artworkKind: 'logo',
            title: `Synced from ${label}`,
            message: formatDjImportSummary(result, { app: label, noun }),
            action: {
                label: 'Turn on Auto-sync',
                onClick: () => {
                    dispatch(updateAutoSyncPref({ app, enabled: true }));
                    track('dj_auto_sync_enabled', { app, from: 'nudge', include_new: false });
                    toast.success(`Auto-sync is on — ${label} changes now import automatically`);
                },
            },
            duration: 12000,
        });
        return true;
    }, [app, label, noun, logo, enabled, userId, dispatch, toast]);
}
